import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  signal,
  Type,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  DialogService,
  DynamicDialogRef,
} from '../../../../core/services/dialog';
import { CacheService } from '../../../../core/services/cache/cache.service';
import { TranslationService } from '../../../../core/services/translation/translation.service';
import { UserType } from '../../../../shared/interfaces/user.model';
import { TranslatePipe } from '../../../pipes/translate/translate.pipe';
import { WidgetItemSelectorComponent } from '../widget-item-selector/widget-item-selector.component';

import { LocalStorageService } from '../../../../core/services/local-storage/local-storage.service';
import { UtilitiesService } from '../../../../core/services/utilities/utilities.service';
import {
  WidgetItem,
  WidgetItemType,
  WidgetService,
  WidgetType,
} from '../../../../core/services/widget/widget.service';
import { PaginatedQueryOptions } from '../../../../shared/models/common.model';
import { UserGuidanceService } from '../../../services/analytics/user-guidance.service';
import { SharedPopoverComponent } from '../../popover/popover.component';
import { IcBrokerSolidComponent } from '../../Icons/ic-broker-solid/ic-broker-solid';
import { IcChevronRightComponent } from '../../Icons/ic-chevron-right/ic-chevron-right';
import { IcClientSolidComponent } from '../../Icons/ic-client-solid/ic-client-solid';

import { CloseIcon } from '../../Icons/ic-close/ic-close';
import { IcCompassComponent } from '../../Icons/ic-compass/ic-compass';
import { IcHomeComponent } from '../../Icons/ic-home';
import { IcParcelBoxComponent } from '../../Icons/ic-parcelbox/ic-parcelbox';
import { IcPlaneComponent } from '../../Icons/ic-plane/ic-plane';
import { IcDeclarationTemplate } from '../../Icons/settings-icon/ic-declaration-template/ic-declaration-template';
import { IcManiestTemplate } from '../../Icons/settings-icon/ic-manifest-template/ic-manifest-template';

import { IcPlusComponent } from '../../Icons/ic-plus/ic-plus';
import { IcSparklesComponent } from '../../Icons/ic-sparkles/ic-sparkles';
import { IcUserRoleSolidComponent } from '../../Icons/ic-user-role-solid/ic-user-role-solid';
import { IcUserComponent } from '../../Icons/ic-user/ic-user';

export interface AvailableWidget {
  label: string;
  type: WidgetType;
  itemType: WidgetItemType;
  icon: Type<unknown>;
}

/** Widget add-menu entry for the add-widget popover. */
export interface WidgetMenuEntry {
  label?: string;
  separator?: boolean;
  command?: () => void;
}

/**
 * WidgetMenuContentComponent component.
 * Provides UI presentation and event handling for widget menu content component.
 */
@Component({
  selector: 'app-widget-menu-content',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    DragDropModule,
    MatTooltipModule,
    SharedPopoverComponent,
    IcPlusComponent,
    CloseIcon,
    IcCompassComponent,
    IcChevronRightComponent,
    IcHomeComponent,
    IcPlaneComponent,
    IcParcelBoxComponent,
    IcBrokerSolidComponent,
    IcClientSolidComponent,
    TranslatePipe,
  ],
  templateUrl: './widget-menu-content.component.html',
  styleUrl: '../widget-menu/widget-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class WidgetMenuContentComponent implements AfterViewInit {
  /**
   * Reference to the home link element or component.
   */
  @ViewChild('homeLink') homeLink!: ElementRef<HTMLAnchorElement>;

  /** Injected service for widget service. */
  public widgetService = inject(WidgetService);
  /** Injected service for translate service. */
  private translateService = inject(TranslationService);
  /** Injected service for router. */
  private router = inject(Router);
  /** Injected service for utilities. */
  private utilities = inject(UtilitiesService);
  /** Injected service for dialog service. */
  private dialogService = inject(DialogService);
  /** Injected service for store. */
  private store = inject(LocalStorageService);
  /** Injected service for guidance service. */
  public guidanceService = inject(UserGuidanceService);
  /** Injected service for cache. */
  private cache = inject(CacheService);
  /** Injected service for ref. */
  public ref: DynamicDialogRef<WidgetMenuContentComponent> =
    inject(DynamicDialogRef);
  /** Property widget shortcut. */
  public widgetShortcut = this.utilities.getPlatformShortcut('⇧W');

  /** Property available widgets. */
  availableWidgets: AvailableWidget[] = [
    {
      label: 'shipmentsList',
      type: 'list',
      itemType: 'shipments',
      icon: IcPlaneComponent,
    },
    {
      label: 'declarationsList',
      type: 'list',
      itemType: 'declarations',
      icon: IcParcelBoxComponent,
    },
    {
      label: 'brokersList',
      type: 'list',
      itemType: 'brokers',
      icon: IcBrokerSolidComponent,
    },
    {
      label: 'clientsList',
      type: 'list',
      itemType: 'clients',
      icon: IcClientSolidComponent,
    },
    {
      label: 'usersList',
      type: 'list',
      itemType: 'users',
      icon: IcUserComponent,
    },
    {
      label: 'manifestTemplates',
      type: 'list',
      itemType: 'manifestTemplates',
      icon: IcManiestTemplate,
    },
    {
      label: 'templates',
      type: 'list',
      itemType: 'declarationTemplates',
      icon: IcDeclarationTemplate,
    },

    {
      label: 'rolesList',
      type: 'list',
      itemType: 'roles',
      icon: IcUserRoleSolidComponent,
    },
  ];

  /** Property item types. */
  itemTypes: {
    label: string;
    value: WidgetItemType;
    apiUrl: string;
    optionLabel: string;
    opts?: PaginatedQueryOptions;
  }[] = [
    {
      label: 'shipments',
      value: 'shipments',
      apiUrl: 'shipment',
      optionLabel: 'shipmentNo',
    },
    {
      label: 'declarations',
      value: 'declarations',
      apiUrl: 'declarations',
      optionLabel: 'declaration-display',
    },
    {
      label: 'brokers',
      value: 'brokers',
      apiUrl: 'company',
      optionLabel: 'name',
      opts: {
        page: 1,
        specificValueFields: JSON.stringify({ type: 'broker' }),
      },
    },
    {
      label: 'clients',
      value: 'clients',
      apiUrl: 'company',
      optionLabel: 'name',
      opts: {
        page: 1,
        specificValueFields: JSON.stringify({ type: 'client' }),
      },
    },
    {
      label: 'users',
      value: 'users',
      apiUrl: 'user',
      optionLabel: 'user-display',
    },
  ];

  /** Property items for dropdown. */
  itemsForDropdown = signal<{ label: string; value: string }[]>([]);

  /** Property widget menu items. */
  widgetMenuItems: WidgetMenuEntry[] = [];
  /** Property is broker. */
  isBroker = false;
  /** Property is client. */
  isClient = false;

  constructor() {
    this.filterOptionsByUserRole();
    this.initMenuItems();
  }
  /**
   * Handles filter options by user role logic.
   */
  private filterOptionsByUserRole() {
    const user = this.cache.currentUser();
    if (!user) return;

    this.isBroker = user.userType === UserType.broker;
    this.isClient = user.userType === UserType.client;

    if (this.isBroker) {
      this.availableWidgets = this.availableWidgets.filter(
        (w) => w.itemType !== 'brokers'
      );
      this.itemTypes = this.itemTypes.filter((it) => it.value !== 'brokers');
    } else if (this.isClient) {
      // Client users never see Brokers; Clients remains when client.read is granted.
      this.availableWidgets = this.availableWidgets.filter(
        (w) => w.itemType !== 'brokers'
      );
      this.itemTypes = this.itemTypes.filter((it) => it.value !== 'brokers');
    }
  }

  /**
   * Handles init menu items logic.
   */
  private initMenuItems() {
    const findList = (itemType: WidgetItemType) =>
      this.availableWidgets.find((w) => w.itemType === itemType);
    const findItem = (value: WidgetItemType) =>
      this.itemTypes.find((it) => it.value === value);

    const createModuleItems = (itemType: WidgetItemType) => {
      const items: WidgetMenuEntry[] = [];
      const listWidget = findList(itemType);
      const itemTypeConfig = findItem(itemType);

      if (listWidget) {
        items.push({
          label: this.translateService.translate(listWidget.label),
          command: () => this.addWidget(listWidget),
        });
      }

      if (itemTypeConfig) {
        items.push({
          label: this.translateService.translate(itemTypeConfig.label),
          command: () => this.openItemSelector(itemTypeConfig),
        });
      }
      return items;
    };

    const filteredMenu: WidgetMenuEntry[] = [
      ...createModuleItems('shipments'),
      { separator: true },
      ...createModuleItems('declarations'),
    ];

    if (!this.isBroker && !this.isClient) {
      filteredMenu.push({ separator: true }, ...createModuleItems('brokers'));
    }

    if (!this.isClient) {
      filteredMenu.push({ separator: true }, ...createModuleItems('clients'));
    }

    filteredMenu.push(
      { separator: true },
      ...createModuleItems('users'),
      {
        label: this.translateService.translate('rolesList'),
        command: () => {
          const rolesWidget = this.availableWidgets.find(
            (w) => w.itemType === 'roles'
          );
          if (rolesWidget) this.addWidget(rolesWidget);
        },
      },
      { separator: true },
      {
        label: this.translateService.translate('manifestTemplates'),
        command: () => {
          const w = this.availableWidgets.find(
            (w) => w.itemType === 'manifestTemplates'
          );
          if (w) this.addWidget(w);
        },
      },
      {
        label: this.translateService.translate('templates'),
        command: () => {
          const w = this.availableWidgets.find(
            (w) => w.itemType === 'declarationTemplates'
          );
          if (w) this.addWidget(w);
        },
      }
    );

    this.widgetMenuItems = filteredMenu;
  }

  /**
   * Runs a widget add-menu entry command when the user selects it.
   * @param item - Selected add-menu entry
   */
  onAddMenuItemClick(item: WidgetMenuEntry): void {
    item.command?.();
  }

  /**
   * Handles add widget logic.
   * @param widget - The widget
   */
  addWidget(widget: AvailableWidget) {
    this.widgetService.addWidget({
      id: Math.random().toString(36).substring(7),
      type: widget.type,
      label: widget.label,
      itemType: widget.itemType,
      icon: widget.icon,
    });
  }

  /**
   * Handles on item type change logic.
   * @param type - The type
   */
  onItemTypeChange(type: WidgetItemType) {
    if (type === 'shipments') {
      return [
        { label: 'SHP-001', value: 'SHP-001' },
        { label: 'SHP-002', value: 'SHP-002' },
      ];
    } else {
      return [
        { label: 'Item A', value: 'A' },
        { label: 'Item B', value: 'B' },
      ];
    }
  }

  /**
   * Handles open item selector logic.
   * @param itemType - The item type
   */
  openItemSelector(itemType: (typeof this.itemTypes)[0]): void {
    const shipmentWidgetIds =
      itemType.value === 'shipments'
        ? this.widgetService
            .activeWidgets()
            .filter(
              (widget: WidgetItem) =>
                widget.type === 'item' &&
                widget.itemType === 'shipments' &&
                widget.itemId != null
            )
            .map((widget: WidgetItem) => String(widget.itemId))
        : [];

    const dialogRef = this.dialogService.open(WidgetItemSelectorComponent, {
      data: {
        type: itemType.value,
        apiUrl: itemType.apiUrl,
        optionLabel: itemType.optionLabel,
        opts: itemType.opts,
        labelKey: itemType.label,
        excludeIds: shipmentWidgetIds,
      },
      header: '',
      width: '450px',
      contentStyle: { padding: '0', overflow: 'hidden', borderRadius: '16px' },
      baseZIndex: 10001,
      modal: true,
      closable: true,
      showHeader: false,
      styleClass: 'item-selector-dialog',
      maskStyleClass: 'widget-item-selector-mask',
      dismissableMask: true,
      closeOnEscape: true,
      transitionOptions: '0ms',
    });

    if (dialogRef) {
      dialogRef.onClose.subscribe((selectedItem: Record<string, unknown>) => {
        if (selectedItem) {
          const typeVal = itemType.value;
          let label = '';

          if (typeVal === 'shipments')
            label = String(selectedItem['shipmentNo'] ?? '');
          else if (typeVal === 'declarations')
            label = String(selectedItem['lrn'] ?? '');
          else if (typeVal === 'users')
            label =
              `${String(selectedItem['firstName'] ?? '')} ${String(
                selectedItem['lastName'] ?? ''
              )}`.trim() || String(selectedItem['username'] ?? '');
          else label = String(selectedItem['name'] ?? itemType.label);

          const itemId = selectedItem['id'];
          if (itemId == null) {
            return;
          }

          this.widgetService.addWidget({
            id: Math.random().toString(36).substring(7),
            type: 'item',
            label: label,
            itemType: typeVal,
            itemId: String(itemId),
          });
        }
      });
    }
  }

  /**
   * Handles on drop logic.
   * @param event - The event
   */
  onDrop(event: CdkDragDrop<WidgetItem[]>) {
    const widgets = [...this.widgetService.activeWidgets()];
    moveItemInArray(widgets, event.previousIndex, event.currentIndex);
    this.widgetService.reorderWidgets(widgets);
  }

  /**
   * Handles get icon logic.
   * @param widget - The widget
   */
  getIcon(widget: WidgetItem): Type<unknown> | null {
    if (widget.icon) return widget.icon;
    const match = this.availableWidgets.find(
      (aw) => aw.itemType === widget.itemType
    );
    return match ? match.icon : IcSparklesComponent;
  }

  /**
   * Handles navigate to widget logic.
   * @param widget - The widget
   */
  navigateToWidget(widget: WidgetItem) {
    this.ref?.close();

    if (widget.itemType === 'userGuide') {
      this.guidanceService.openAssistant();
      return;
    }

    if (widget.type === 'item' && widget.itemId) {
      this.store.setItem('SELECTED_ID', widget.itemId);
      const id = widget.itemId;

      switch (widget.itemType) {
        case 'shipments':
          this.router.navigate(['/shipments/details', id, 'info']);
          break;
        case 'declarations':
          this.router.navigate(['/declarations/details', id, 'info']);
          break;
        case 'brokers':
          this.router.navigate(['/settings/brokers/details', id, 'info']);
          break;
        case 'clients':
          this.router.navigate(['/settings/clients/details', id, 'info']);
          break;
        case 'users':
          this.router.navigate(['/settings/users/details', id]);
          break;
      }
    } else if (widget.type === 'list') {
      if (widget.itemType === 'shipments') {
        this.router.navigate(['/shipments/all']);
      } else if (widget.itemType === 'declarations') {
        this.router.navigate(['/declarations/all']);
      } else if (widget.itemType === 'brokers') {
        this.router.navigate(['/settings/brokers']);
      } else if (widget.itemType === 'clients') {
        this.router.navigate(['/settings/clients']);
      } else if (widget.itemType === 'users') {
        this.router.navigate(['/settings/users']);
      } else if (widget.itemType === 'manifestTemplates') {
        this.router.navigate(['/settings/manifest-templates']);
      } else if (widget.itemType === 'declarationTemplates') {
        this.router.navigate(['/settings/declaration-templates']);
      } else if (widget.itemType === 'roles') {
        this.router.navigate(['/settings/roles']);
      }
    }
  }

  /**
   * Lifecycle hook that runs after component view initialization.
   */
  ngAfterViewInit() {
    // Short delay to ensure PrimeNG dialog focus-trap is ready
    setTimeout(() => {
      this.homeLink?.nativeElement?.focus();
    }, 100);
  }

  /**
   * Handles close logic.
   */
  close() {
    this.ref?.close();
  }
}
